import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, unref as _unref, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "report-editor" }
const _hoisted_2 = { class: "left-sidebar" }
const _hoisted_3 = { class: "sidebar-section" }
const _hoisted_4 = { class: "pages-list" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "page-number" }
const _hoisted_7 = { class: "page-actions" }
const _hoisted_8 = ["onClick", "disabled"]
const _hoisted_9 = ["onClick", "disabled"]
const _hoisted_10 = ["onClick", "disabled"]
const _hoisted_11 = { class: "main-content" }
const _hoisted_12 = { class: "content-header" }
const _hoisted_13 = { class: "content-actions" }
const _hoisted_14 = { class: "page-indicator" }
const _hoisted_15 = { class: "pages-container" }
const _hoisted_16 = { class: "page-wrapper" }
const _hoisted_17 = {
  key: 0,
  class: "a4-page"
}
const _hoisted_18 = {
  key: 0,
  class: "page-header"
}
const _hoisted_19 = { class: "header-content" }
const _hoisted_20 = {
  key: 1,
  class: "page-content"
}
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "block-controls" }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = ["onClick", "disabled"]
const _hoisted_25 = ["onClick", "disabled"]
const _hoisted_26 = {
  key: 2,
  class: "page-footer"
}
const _hoisted_27 = {
  key: 1,
  class: "loading-page"
}
const _hoisted_28 = {
  key: 0,
  class: "page-navigation"
}
const _hoisted_29 = ["disabled"]
const _hoisted_30 = ["disabled"]
const _hoisted_31 = {
  key: 0,
  class: "right-sidebar"
}
const _hoisted_32 = { class: "sidebar-header" }
const _hoisted_33 = { class: "edit-panel-content" }
const _hoisted_34 = {
  key: 1,
  class: "right-sidebar empty"
}

import { ref, reactive, computed, onMounted, watch } from "vue";
import draggable from "vuedraggable";

// Components
import TextBlock from "./components/blocks/TextBlock.vue";
import DataBlock from "./components/blocks/DataBlock.vue";
import TableBlock from "./components/blocks/TableBlock.vue";
import ImageBlock from "./components/blocks/ImageBlock.vue";
import TextEdit from "./components/editors/TextEdit.vue";
import DataEdit from "./components/editors/DataEdit.vue";
import TableEdit from "./components/editors/TableEdit.vue";
import ImageEdit from "./components/editors/ImageEdit.vue";

// Interfaces
interface Block {
  id: string;
  type: "text" | "data" | "table" | "image";
  data: any;
  style: any;
}

interface Page {
  id: string;
  header?: {
    title: string;
    subtitle: string;
  };
  footer?: {
    content: string;
  };
  blocks: Block[];
}

interface Report {
  title: string;
  pages: Page[];
  formData: any;
}

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    formData: {}
  },
  setup(__props: any) {

const props = __props;

// Estado
const report = reactive<Report>({
  title: "Relatório de Higiene Ocupacional",
  pages: [],
  formData: {},
});

const selectedBlock = ref<Block | null>(null);
const currentPageIndex = ref(0);
const isLoading = ref(true);

// Computed com proteção contra undefined
const currentPage = computed(() => {
  return report.pages[currentPageIndex.value] || createNewPage();
});

// Inicialização
onMounted(() => {
  initializeReport();
});

// Métodos (mantidos do código anterior corrigido)
function initializeReport() {
  try {
    isLoading.value = true;
    report.formData = props.formData || {};

    if (report.pages.length === 0) {
      report.pages = [createNewPage()];
    }

    if (!report.pages[currentPageIndex.value]) {
      currentPageIndex.value = 0;
    }

    const firstPage = report.pages[0];
    if (firstPage.blocks.length === 0) {
      firstPage.blocks.push(
        createTextBlock("<h2>Relatório de Higiene Ocupacional</h2>")
      );
      firstPage.blocks.push(
        createDataBlock("dados-gerais", "Dados Gerais da Avaliação")
      );
      firstPage.blocks.push(
        createTableBlock("parametros", "Parâmetros Avaliados")
      );
      firstPage.blocks.push(
        createTextBlock(
          "<h3>Observações e Recomendações</h3><p>Digite as observações aqui...</p>"
        )
      );
    }
  } catch (error) {
    console.error("Erro ao inicializar relatório:", error);
    report.pages = [createNewPage()];
  } finally {
    isLoading.value = false;
  }
}

function createNewPage(): Page {
  return {
    id: generateId(),
    header: {
      title: "Relatório de Higiene Ocupacional",
      subtitle: "Sistema de Gestão de Higiene Ocupacional",
    },
    footer: {
      content: `Página 1 | Emitido em ${new Date().toLocaleDateString("pt-BR")}`,
    },
    blocks: [],
  };
}

function createTextBlock(content: string): Block {
  return {
    id: generateId(),
    type: "text",
    data: { content },
    style: { fontSize: "14px", lineHeight: "1.6" },
  };
}

function createDataBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: "data",
    data: {
      dataKey,
      title,
      fields: getDataFields(dataKey),
    },
    style: { backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "5px" },
  };
}

function createTableBlock(dataKey: string, title: string): Block {
  return {
    id: generateId(),
    type: "table",
    data: {
      dataKey,
      title,
      columns: getTableColumns(dataKey),
      data: getTableData(dataKey),
    },
    style: { margin: "20px 0" },
  };
}

function createImageBlock(): Block {
  return {
    id: generateId(),
    type: "image",
    data: { src: "", alt: "Imagem do relatório", caption: "" },
    style: { textAlign: "center", margin: "20px 0" },
  };
}

// Funções auxiliares com proteção
function getDataFields(dataKey: string) {
  const fieldsMap: any = {
    "dados-gerais": [
      { label: "Local", value: report.formData?.local || "" },
      {
        label: "Data da Avaliação",
        value: report.formData?.assessmentDate || "",
      },
      { label: "Avaliador", value: report.formData?.evaluator || "" },
      { label: "Empresa", value: report.formData?.company || "" },
    ],
  };
  return fieldsMap[dataKey] || [];
}

function getTableColumns(dataKey: string) {
  const columnsMap: any = {
    parametros: [
      { key: "parameter", label: "Parâmetro", editable: true },
      { key: "value", label: "Valor Medido", editable: true },
      { key: "limit", label: "Limite", editable: true },
      { key: "unit", label: "Unidade", editable: true },
      { key: "status", label: "Status", editable: false },
    ],
  };
  return columnsMap[dataKey] || [];
}

function getTableData(dataKey: string) {
  const dataMap: any = {
    parametros: (report.formData?.parameters || []).map((param: any) => ({
      parameter: param.name || "",
      value: param.measuredValue || "",
      limit: param.limit || "",
      unit: param.unit || "",
      status:
        param.measuredValue && param.limit && param.measuredValue <= param.limit
          ? "Dentro"
          : "Acima",
    })),
  };
  return dataMap[dataKey] || [];
}

// Métodos de Navegação
function previousPage() {
  if (currentPageIndex.value > 0) {
    currentPageIndex.value--;
    selectedBlock.value = null;
  }
}

function nextPage() {
  if (currentPageIndex.value < report.pages.length - 1) {
    currentPageIndex.value++;
    selectedBlock.value = null;
  }
}

// Métodos do Editor
function addPage() {
  const newPage = createNewPage();
  report.pages.push(newPage);
  currentPageIndex.value = report.pages.length - 1;
}

function removePage(pageIndex: number) {
  if (report.pages.length > 1) {
    report.pages.splice(pageIndex, 1);
    if (currentPageIndex.value >= pageIndex && currentPageIndex.value > 0) {
      currentPageIndex.value--;
    }
  }
}

function movePageUp(pageIndex: number) {
  if (pageIndex > 0) {
    const page = report.pages.splice(pageIndex, 1)[0];
    report.pages.splice(pageIndex - 1, 0, page);
    if (currentPageIndex.value === pageIndex) {
      currentPageIndex.value = pageIndex - 1;
    }
  }
}

function movePageDown(pageIndex: number) {
  if (pageIndex < report.pages.length - 1) {
    const page = report.pages.splice(pageIndex, 1)[0];
    report.pages.splice(pageIndex + 1, 0, page);
    if (currentPageIndex.value === pageIndex) {
      currentPageIndex.value = pageIndex + 1;
    }
  }
}

function addTextBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(
      createTextBlock("<p>Digite seu texto aqui...</p>")
    );
  }
}

function addDataBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(
      createDataBlock("custom-data", "Dados Personalizados")
    );
  }
}

function addTableBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(
      createTableBlock("custom-table", "Tabela Personalizada")
    );
  }
}

function addImageBlock() {
  if (currentPage.value) {
    currentPage.value.blocks.push(createImageBlock());
  }
}

function removeBlock(pageIndex: number, blockIndex: number) {
  if (report.pages[pageIndex] && report.pages[pageIndex].blocks) {
    report.pages[pageIndex].blocks.splice(blockIndex, 1);
    selectedBlock.value = null;
  }
}

function moveBlockUp(pageIndex: number, blockIndex: number) {
  if (
    blockIndex > 0 &&
    report.pages[pageIndex] &&
    report.pages[pageIndex].blocks
  ) {
    const blocks = report.pages[pageIndex].blocks;
    const block = blocks.splice(blockIndex, 1)[0];
    blocks.splice(blockIndex - 1, 0, block);
  }
}

function moveBlockDown(pageIndex: number, blockIndex: number) {
  const blocks = report.pages[pageIndex]?.blocks;
  if (blocks && blockIndex < blocks.length - 1) {
    const block = blocks.splice(blockIndex, 1)[0];
    blocks.splice(blockIndex + 1, 0, block);
  }
}

function selectBlock(block: Block) {
  selectedBlock.value = block;
}

function updateBlockData(pageIndex: number, blockIndex: number, newData: any) {
  if (report.pages[pageIndex] && report.pages[pageIndex].blocks[blockIndex]) {
    report.pages[pageIndex].blocks[blockIndex].data = {
      ...report.pages[pageIndex].blocks[blockIndex].data,
      ...newData,
    };
  }
}

function onBlockEdit(updatedBlock: Block) {
  if (selectedBlock.value) {
    Object.assign(selectedBlock.value, updatedBlock);
  }
}

function onContentEdit() {
  saveReport();
}

function onBlockReorder() {
  saveReport();
}

function updateHeader(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement;
  if (report.pages[pageIndex]?.header) {
    report.pages[pageIndex].header!.title = target.innerText;
  }
}

function updateSubtitle(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement;
  if (report.pages[pageIndex]?.header) {
    report.pages[pageIndex].header!.subtitle = target.innerText;
  }
}

function updateFooter(pageIndex: number, event: Event) {
  const target = event.target as HTMLElement;
  if (report.pages[pageIndex]?.footer) {
    report.pages[pageIndex].footer!.content = target.innerText;
  }
}

// Componentes dinâmicos
function getBlockComponent(type: string) {
  const components: any = {
    text: TextBlock,
    data: DataBlock,
    table: TableBlock,
    image: ImageBlock,
  };
  return components[type] || TextBlock;
}

function getEditComponent(type: string) {
  const components: any = {
    text: TextEdit,
    data: DataEdit,
    table: TableEdit,
    image: ImageEdit,
  };
  return components[type] || TextEdit;
}

// Utilitários
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

function saveReport() {
  try {
    localStorage.setItem("occupational-hygiene-report", JSON.stringify(report));
    console.log("Relatório salvo:", report);
  } catch (error) {
    console.error("Erro ao salvar relatório:", error);
  }
}

function exportPDF() {
  alert("Exportando para PDF...");
}

function printReport() {
  window.print();
}

// Watch para garantir que currentPageIndex seja sempre válido
watch(currentPageIndex, (newIndex) => {
  if (newIndex >= report.pages.length) {
    currentPageIndex.value = Math.max(0, report.pages.length - 1);
  }
});

watch(
  () => report.pages,
  (newPages) => {
    if (newPages.length === 0) {
      report.pages.push(createNewPage());
    }
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[16] || (_cache[16] = _createElementVNode("div", { class: "sidebar-header" }, [
        _createElementVNode("h3", null, "Editor de Relatório")
      ], -1)),
      _createElementVNode("div", { class: "sidebar-section" }, [
        _cache[9] || (_cache[9] = _createElementVNode("h4", null, "Adicionar Blocos", -1)),
        _createElementVNode("div", { class: "block-buttons" }, [
          _createElementVNode("button", {
            onClick: addTextBlock,
            class: "block-btn"
          }, [...(_cache[5] || (_cache[5] = [
            _createElementVNode("span", { class: "block-icon" }, "📝", -1),
            _createElementVNode("span", { class: "block-label" }, "Texto", -1)
          ]))]),
          _createElementVNode("button", {
            onClick: addDataBlock,
            class: "block-btn"
          }, [...(_cache[6] || (_cache[6] = [
            _createElementVNode("span", { class: "block-icon" }, "📊", -1),
            _createElementVNode("span", { class: "block-label" }, "Dados", -1)
          ]))]),
          _createElementVNode("button", {
            onClick: addTableBlock,
            class: "block-btn"
          }, [...(_cache[7] || (_cache[7] = [
            _createElementVNode("span", { class: "block-icon" }, "📋", -1),
            _createElementVNode("span", { class: "block-label" }, "Tabela", -1)
          ]))]),
          _createElementVNode("button", {
            onClick: addImageBlock,
            class: "block-btn"
          }, [...(_cache[8] || (_cache[8] = [
            _createElementVNode("span", { class: "block-icon" }, "🖼️", -1),
            _createElementVNode("span", { class: "block-label" }, "Imagem", -1)
          ]))])
        ])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _cache[11] || (_cache[11] = _createElementVNode("h4", null, "Gerenciar Páginas", -1)),
        _createElementVNode("div", { class: "page-controls" }, [
          _createElementVNode("button", {
            onClick: addPage,
            class: "sidebar-btn primary"
          }, [...(_cache[10] || (_cache[10] = [
            _createElementVNode("span", null, "📄", -1),
            _createTextVNode(" Nova Página ", -1)
          ]))])
        ]),
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(report.pages, (page, pageIndex) => {
            return (_openBlock(), _createElementBlock("div", {
              key: page.id,
              class: _normalizeClass(["page-item", { active: currentPageIndex.value === pageIndex }]),
              onClick: ($event: any) => (currentPageIndex.value = pageIndex)
            }, [
              _createElementVNode("span", _hoisted_6, "Página " + _toDisplayString(pageIndex + 1), 1),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("button", {
                  onClick: _withModifiers(($event: any) => (movePageUp(pageIndex)), ["stop"]),
                  class: "page-action-btn",
                  disabled: pageIndex === 0
                }, " ↑ ", 8, _hoisted_8),
                _createElementVNode("button", {
                  onClick: _withModifiers(($event: any) => (movePageDown(pageIndex)), ["stop"]),
                  class: "page-action-btn",
                  disabled: pageIndex === report.pages.length - 1
                }, " ↓ ", 8, _hoisted_9),
                _createElementVNode("button", {
                  onClick: _withModifiers(($event: any) => (removePage(pageIndex)), ["stop"]),
                  class: "page-action-btn danger",
                  disabled: report.pages.length === 1
                }, " × ", 8, _hoisted_10)
              ])
            ], 10, _hoisted_5))
          }), 128))
        ])
      ]),
      _createElementVNode("div", { class: "sidebar-section" }, [
        _cache[15] || (_cache[15] = _createElementVNode("h4", null, "Ações", -1)),
        _createElementVNode("div", { class: "action-buttons" }, [
          _createElementVNode("button", {
            onClick: exportPDF,
            class: "sidebar-btn success"
          }, [...(_cache[12] || (_cache[12] = [
            _createElementVNode("span", null, "📤", -1),
            _createTextVNode(" Exportar PDF ", -1)
          ]))]),
          _createElementVNode("button", {
            onClick: saveReport,
            class: "sidebar-btn"
          }, [...(_cache[13] || (_cache[13] = [
            _createElementVNode("span", null, "💾", -1),
            _createTextVNode(" Salvar ", -1)
          ]))]),
          _createElementVNode("button", {
            onClick: printReport,
            class: "sidebar-btn"
          }, [...(_cache[14] || (_cache[14] = [
            _createElementVNode("span", null, "🖨️", -1),
            _createTextVNode(" Imprimir ", -1)
          ]))])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, [
        _cache[17] || (_cache[17] = _createElementVNode("h2", null, "Relatório de Higiene Ocupacional", -1)),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("span", _hoisted_14, " Página " + _toDisplayString(currentPageIndex.value + 1) + " de " + _toDisplayString(report.pages.length), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          (currentPage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                (currentPage.value.header)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                      _createElementVNode("div", _hoisted_19, [
                        _createElementVNode("h1", {
                          contenteditable: "true",
                          onBlur: _cache[0] || (_cache[0] = ($event: any) => (updateHeader(currentPageIndex.value, $event))),
                          onInput: onContentEdit
                        }, _toDisplayString(currentPage.value.header.title), 33),
                        _createElementVNode("div", {
                          contenteditable: "true",
                          class: "header-subtitle",
                          onBlur: _cache[1] || (_cache[1] = ($event: any) => (updateSubtitle(currentPageIndex.value, $event))),
                          onInput: onContentEdit
                        }, _toDisplayString(currentPage.value.header.subtitle), 33)
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (currentPage.value.blocks)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                      _createVNode(_unref(draggable), {
                        modelValue: currentPage.value.blocks,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((currentPage.value.blocks) = $event)),
                        "item-key": "id",
                        class: "blocks-container",
                        onEnd: onBlockReorder,
                        handle: ".drag-handle"
                      }, {
                        item: _withCtx(({ element: block, index: blockIndex }) => [
                          _createElementVNode("div", {
                            class: _normalizeClass([
                      'block',
                      `block-${block.type}`,
                      { selected: selectedBlock.value?.id === block.id },
                    ]),
                            onClick: ($event: any) => (selectBlock(block))
                          }, [
                            _createElementVNode("div", _hoisted_22, [
                              _cache[18] || (_cache[18] = _createElementVNode("div", { class: "drag-handle" }, "⠿", -1)),
                              _createElementVNode("button", {
                                onClick: _withModifiers(($event: any) => (removeBlock(currentPageIndex.value, blockIndex)), ["stop"]),
                                class: "block-btn danger"
                              }, " × ", 8, _hoisted_23),
                              _createElementVNode("button", {
                                onClick: _withModifiers(($event: any) => (moveBlockUp(currentPageIndex.value, blockIndex)), ["stop"]),
                                class: "block-btn",
                                disabled: blockIndex === 0
                              }, " ↑ ", 8, _hoisted_24),
                              _createElementVNode("button", {
                                onClick: _withModifiers(($event: any) => (
                          moveBlockDown(currentPageIndex.value, blockIndex)
                        ), ["stop"]),
                                class: "block-btn",
                                disabled: blockIndex === currentPage.value.blocks.length - 1
                              }, " ↓ ", 8, _hoisted_25)
                            ]),
                            (_openBlock(), _createBlock(_resolveDynamicComponent(getBlockComponent(block.type)), {
                              block: block,
                              pageIndex: currentPageIndex.value,
                              blockIndex: blockIndex,
                              onUpdate: ($event: any) => (
                        updateBlockData(currentPageIndex.value, blockIndex, $event)
                      ),
                              onContentEdit: onContentEdit
                            }, null, 40, ["block", "pageIndex", "blockIndex", "onUpdate"]))
                          ], 10, _hoisted_21)
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]))
                  : _createCommentVNode("", true),
                (currentPage.value.footer)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                      _createElementVNode("div", {
                        contenteditable: "true",
                        class: "footer-content",
                        onBlur: _cache[3] || (_cache[3] = ($event: any) => (updateFooter(currentPageIndex.value, $event))),
                        onInput: onContentEdit
                      }, _toDisplayString(currentPage.value.footer.content), 33)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_27, [...(_cache[19] || (_cache[19] = [
                _createElementVNode("p", null, "Carregando página...", -1)
              ]))]))
        ])
      ]),
      (report.pages.length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
            _createElementVNode("button", {
              onClick: previousPage,
              class: "nav-btn",
              disabled: currentPageIndex.value === 0
            }, " ← Página Anterior ", 8, _hoisted_29),
            _createElementVNode("button", {
              onClick: nextPage,
              class: "nav-btn",
              disabled: currentPageIndex.value === report.pages.length - 1
            }, " Próxima Página → ", 8, _hoisted_30)
          ]))
        : _createCommentVNode("", true)
    ]),
    (selectedBlock.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
          _createElementVNode("div", _hoisted_32, [
            _cache[20] || (_cache[20] = _createElementVNode("h3", null, "Editar Bloco", -1)),
            _createElementVNode("button", {
              onClick: _cache[4] || (_cache[4] = ($event: any) => (selectedBlock.value = null)),
              class: "close-btn"
            }, "×")
          ]),
          _createElementVNode("div", _hoisted_33, [
            (_openBlock(), _createBlock(_resolveDynamicComponent(getEditComponent(selectedBlock.value.type)), {
              block: selectedBlock.value,
              onUpdate: onBlockEdit
            }, null, 40, ["block"]))
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_34, [...(_cache[21] || (_cache[21] = [
          _createElementVNode("div", { class: "empty-state" }, [
            _createElementVNode("div", { class: "empty-icon" }, "📝"),
            _createElementVNode("h3", null, "Selecione um Bloco"),
            _createElementVNode("p", null, "Clique em qualquer bloco para começar a editar")
          ], -1)
        ]))]))
  ]))
}
}

})